/* ★ KOSONGKAN PROMPT — butang bar pukal Workspace (2026-09-13)
 *
 * Pelanggan menanda kad, pilih Set 1 / Set 2 / Set 3 / Semua Set, sahkan dalam
 * popup, dan semua teks dalam set itu dikosongkan.
 *
 * Keputusan user 2026-09-13 (ditanya dahulu — jangan ubah tanpa bertanya):
 *   · DIKOSONGKAN : semua teks dalam set — Hook Text, Image Prompt, Video Script,
 *                   Video Prompt, Caption TikTok (termasuk template di sebaliknya)
 *                   serta Full Prompt (`rawOutput`).
 *   · KEKAL       : video, poster, gambar, avatar dan data produk.
 *   · Ikut set (menu corak butang Del Video) + popup pengesahan dahulu.
 *   · Status dan tab kad TIDAK berubah.
 *
 * ⛔⛔ KENAPA TIDAK MELALUI saveToCloud / _stageProductWrite:
 *   Teks kad duduk dalam subdoc `detail/text` (Split V2) yang ditulis dengan
 *   merge. Menghantar kad bersama teks kosong melalui laluan simpan biasa TIDAK
 *   memadam apa-apa, dan gagalnya SENYAP:
 *     (a) `splitProductForWrite` hanya menulis subdoc bila ada kandungan BUKAN
 *         kosong (`hasHeavy`) — kad tanpa data produk langsung tidak ditulis;
 *     (b) dalam merge, kunci yang hilang daripada objek tidak terpadam.
 *   Teks lama "hidup semula" bila halaman dimuat semula. Jadi fail ini memadam
 *   setiap medan TERUS dengan deleteField() pada KEDUA-DUA doc (utama + subdoc) —
 *   corak sama `retryProductPipeline`.
 *
 * Kunci teks ditentukan secara GENERIK: setiap kunci dalam set KECUALI media dan
 * kunci dalaman `_`. Diukur 2026-09-13 pada 612 kad (60 akaun paling baru log
 * masuk): selain uploadedVideo / uploadedVideoThumb, SEMUA kunci set ialah teks —
 * captionTiktok, promptImage, promptVideo, hookText, videoScript, *Template,
 * promptVideoN, videoScriptN, promptVideoNTemplate, promptImageN. Jadi satu
 * peraturan meliputi 1 babak, 2 babak dan N babak tanpa senarai medan yang
 * perlu dikemas kini setiap kali module baharu muncul.
 *
 * ⚠️ KEMBAR: MEDIA_SET = `PROMPT_VIEW_SKIP` / `DUP_MEDIA_FIELDS` dalam
 *    WorkspaceView.jsx + `MEDAN_MEDIA_SET` dalam utils/kadSalinan.js (2026-10-06).
 *    Medan media baharu → tambah di KEEMPAT-EMPAT tempat,
 *    kalau tidak butang ini akan memadamnya sebagai "teks".
 */
import { db } from '../firebase';
import { doc, writeBatch, deleteField, serverTimestamp, FieldPath } from 'firebase/firestore';
import { productDetailDoc } from '../services/productService';

export const SET_KAD = ['set1', 'set2', 'set3'];

const MEDIA_SET = new Set(['uploadedVideo', 'uploadedVideoThumb', 'uploadedImage']);

/* Setiap batch ≤ 200 kad: satu kad = paling banyak 2 operasi (doc utama +
   subdoc), jadi 400 < had Firestore 500. */
const KAD_SETIAP_BATCH = 200;

const setSah = (s) => !!s && typeof s === 'object' && !Array.isArray(s);

export const kunciTeks = (k) => !MEDIA_SET.has(k) && !String(k).startsWith('_');

const kunciTeksSet = (s) => (setSah(s) ? Object.keys(s).filter(kunciTeks) : []);

const teksBerisi = (v) => typeof v === 'string' && v.trim() !== '';

/* Set ini ada teks yang pelanggan NAMPAK? (Kunci kosong tidak dikira.) */
export const setAdaTeks = (s) => setSah(s) && Object.entries(s).some(([k, v]) => kunciTeks(k) && teksBerisi(v));

/* Salinan set tanpa teks — media dan kunci dalaman kekal. */
export const setTanpaTeks = (s) => {
    if (!setSah(s)) return s;
    const out = {};
    for (const [k, v] of Object.entries(s)) if (!kunciTeks(k)) out[k] = v;
    return out;
};

/* 'all' = set1-3 + mana-mana `setN` lain yang memang wujud pada kad. */
const setDalamSkop = (setKey, ...sumber) => {
    if (setKey !== 'all') return [setKey];
    const lain = sumber.flatMap(o => (setSah(o) ? Object.keys(o).filter(k => /^set\d+$/.test(k)) : []));
    return [...new Set([...SET_KAD, ...lain])];
};

/**
 * Pelan untuk SATU kad — medan mana hendak dipadam, dan sama ada kad itu
 * memang ada teks untuk dikosongkan.
 *
 * @param produk  doc utama seperti dalam state (ringan untuk kad splitV 2)
 * @param detail  hasil `loadProductDetail` yang dibaca SEGAR (null untuk kad
 *                bukan split). ⛔ Jangan beri salinan cache: medan yang ditambah
 *                oleh browser lain selepas cache dibaca tidak akan dipadam.
 * @param draf    suntingan belum disimpan bagi kad itu (boleh undefined)
 * @param setKey  'set1' | 'set2' | 'set3' | 'all'
 */
export function pelanKad({ produk, detail = null, draf, setKey }) {
    const utama = setSah(produk?.prompts) ? produk.prompts : {};
    const berat = detail?.exists && setSah(detail.prompts) ? detail.prompts : {};
    const drafP = setSah(draf) ? draf : {};
    const sets = setDalamSkop(setKey, utama, berat, drafP);

    const kunciUtama = [];
    const kunciDetail = [];
    let adaTeks = false;
    for (const s of sets) {
        kunciTeksSet(utama[s]).forEach(k => kunciUtama.push([s, k]));
        kunciTeksSet(berat[s]).forEach(k => kunciDetail.push([s, k]));
        if (setAdaTeks(utama[s]) || setAdaTeks(berat[s]) || setAdaTeks(drafP[s])) adaTeks = true;
    }
    /* Full Prompt bukan milik satu set, jadi ia hanya menjadikan kad "ada teks"
       bila SEMUA set dikosongkan. Ia tetap dipadam bila kad dikosongkan ikut
       set — kalau dibiarkan, Full Prompt terus memapar teks set yang baru
       dikosongkan dan pelanggan membacanya sebagai butang yang tidak menjadi. */
    if (setKey === 'all' && (teksBerisi(produk?.rawOutput) || teksBerisi(detail?.rawOutput))) adaTeks = true;

    return {
        id: produk?.id,
        sets,
        kunciUtama,
        kunciDetail,
        adaTeks,
        adaDetail: !!detail?.exists,
        detail,
    };
}

/**
 * Tulis pelan ke Firestore.
 *
 * Doc utama  → `update` dengan laluan medan (bukan `set`): doc yang sudah dipadam
 *              oleh browser lain menggagalkan batch dengan jelas, bukannya lahir
 *              semula sebagai kad hantu. `lastUpdated` dinaikkan supaya browser
 *              lain membuang cache teks mereka.
 * Subdoc     → `set` merge + deleteField bersarang, hanya bila subdoc memang wujud
 *              (elak mencipta subdoc kosong).
 *
 * @returns {{ berjaya: string[], ralat: Error|null }} — kumpulan yang gagal
 *          tidak membatalkan kumpulan yang sudah berjaya; pemanggil WAJIB
 *          melaporkan kedua-duanya.
 */
export async function kosongkanPromptKad(uid, pelan) {
    const berjaya = [];
    for (let i = 0; i < pelan.length; i += KAD_SETIAP_BATCH) {
        const kumpulan = pelan.slice(i, i + KAD_SETIAP_BATCH);
        const batch = writeBatch(db);
        for (const p of kumpulan) {
            const medan = [];
            for (const [s, k] of p.kunciUtama) medan.push(new FieldPath('prompts', s, k), deleteField());
            medan.push('rawOutput', deleteField(), 'lastUpdated', serverTimestamp());
            batch.update(doc(db, 'users', uid, 'products', p.id), ...medan);

            if (p.adaDetail) {
                const prompts = {};
                for (const [s, k] of p.kunciDetail) {
                    if (!prompts[s]) prompts[s] = {};
                    prompts[s][k] = deleteField();
                }
                batch.set(productDetailDoc(uid, p.id), {
                    ...(Object.keys(prompts).length ? { prompts } : {}),
                    rawOutput: deleteField(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
            }
        }
        try {
            await batch.commit();
            kumpulan.forEach(p => berjaya.push(p.id));
        } catch (ralat) {
            console.error('[KosongkanPrompt] Batch gagal:', ralat);
            return { berjaya, ralat };
        }
    }
    return { berjaya, ralat: null };
}
